import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useJobStore } from "../../../store/jobStore";
import { ReportJobStatus } from "../../../shared/interfaces/JobStatus";
import { NarrativeTemplate } from "../../../shared/interfaces/ReportGenReport";
import { VideoData } from "../../../shared/interfaces/VideoData";
import { jobsClient } from "../../../api/clients";
import { jobStartHitlToolRegistry } from "../../../components/chat/hitl/registry";
import { useHitlEnabled } from "../../../hooks/useHitlEnabled";

export const useReportGeneration = (
  evidenceID: string | undefined,
  reportType: NarrativeTemplate,
  videoDataList: VideoData[],
  isMaxJobsReached: boolean,
  showError: (message: string) => void
) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const addJob = useJobStore((state) => state.addJob);
  const navigate = useNavigate();
  const hitlEnabled = useHitlEnabled();

  const handleGenerateReport = useCallback(() => {
    if (isGenerating) return;
    if (isMaxJobsReached) {
      showError("Maximum number of concurrent jobs reached. Please wait for existing jobs to complete.");
      return;
    }
    setIsGenerating(true);

    const requestBody: Record<string, unknown> = {
      reportType: reportType.template.templateId,
      // The field-selection UI was removed as dead code; the API still expects the key.
      selectedFields: [],
      videos: evidenceID?.split("~"),
      narrativeTemplateId: reportType.template.templateId,
      ...(hitlEnabled ? { hitlToolRegistry: jobStartHitlToolRegistry } : {}),
    };

    const evidenceIDs = evidenceID?.split("~") || [];
    jobsClient
      .post("/jobs", requestBody)
      .then((res) => {
        addJob({
          id: res.data.jobId,
          status: ReportJobStatus.PENDING,
          progress: 0,
          evidenceIDs,
          selectedFields: [],
          videoTitles: videoDataList.map((video) => video.title),
        });
        navigate("/");
      })
      .catch((error) => {
        showError(
          error.response?.data?.error ||
            "An error occurred while generating the report. Please try again later."
        );
      })
      .finally(() => setIsGenerating(false));
  }, [isGenerating, isMaxJobsReached, reportType, evidenceID, addJob, videoDataList, navigate, showError, hitlEnabled]);

  const handleSubmitClick = useCallback(() => {
    handleGenerateReport();
  }, [handleGenerateReport]);

  return { isGenerating, handleSubmitClick };
};
